import "./App.scss";
import "./styles/global.scss"; // Import global styles
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useParams,
} from "react-router-dom";
import { Toaster } from "./components/Toaster";
import { useLoading } from "./contexts/LoadingContext";
import { useAuth } from "./contexts/AuthContext";
import { setupAxiosInterceptors } from "./AxiosInstance";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import LoadingBar from "./components/LoadingBar/LoadingBar";
import SidebarLayout from "./layouts/SidebarLayout";
import SignIn from "./pages/SignIn/SignIn";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import SignUp from "./pages/SignUp/SignUp";
import Jobs from "./pages/Jobs/Jobs";
import NotFound from "./components/NotFound/NotFound";
import ComingSoon from "./components/ComingSoon/ComingSoon";
import Profile from "./pages/Profile/Profile";
import Container from "./components/Container";
import AssignContainers from "./pages/AssignContainers/AssignContainers";
// import Profile from "./pages/Profile";
// import Settings from "./pages/Settings";
// import Reports from "./pages/Reports";
// import Transporter from "./components/Transporter";
import TripHistory from "./pages/TripHistory/TripHistory";
import TripHistoryJob from "./pages/TripHistoryJob/TripHistoryJob";
import initializeAnalytics, { logPageView } from "./utils/analytics";
import initializeClarity from "./utils/clarity";
import ExistingJob from "./components/Jobs/ExistingJob";
import { JobStatusEnum } from "./interfaces/job.interface";
import NewJob from "./components/Jobs/NewJob";
import VersionChecker from "./components/VersionChecker/VersionChecker";
import JobContainers from "./pages/JobContainers/JobContainers";
import ActiveTrips from "./pages/ActiveTrips/ActiveTrips";
import ViewJob from "./pages/ViewJob/ViewJob";
import LiveTracking from "./components/LiveTracking/LiveTracking";
import JobService from "./services/JobService";
import LiveTrack from "./pages/LiveTrack/LiveTrack";
import TripHistoryUpload from "./pages/TripHistoryUpload/TripHistoryUpload";
import JobHistory from "./pages/Jobs/JobHistory";
import JobHistoryUpload from "./pages/JobHistory/JobHistoryUpload";
import Drivers from "./pages/Drivers/Drivers";
import Transporters from "./pages/Transporters/Transporters";
import Instructions from "./pages/Instructions/Instructions";

// const LiveTrackingWrapper = () => {
//   const { jobId, jobCode, tripId } = useParams();
//   const [job, setJob] = useState<any>(null);

//   useEffect(() => {
//     const fetchJob = async () => {
//       try {
//         const response = await JobService.getJobDetails(parseInt(jobId || "0"));
//         if (response.data && !response.data.error) {
//           setJob(response.data.data);
//         }
//       } catch (error) {
//         console.error("Error fetching job:", error);
//       }
//     };

//     if (jobId) {
//       fetchJob();
//     }
//   }, [jobId]);

//   if (!job) {
//     return <div>Loading...</div>;
//   }

//   return (
//     <LiveTracking job={job} jobBreakupDetailId={parseInt(tripId || "0")} />
//   );
// };

const App: React.FC = () => {
  const { isLoading } = useLoading();
  const { logout } = useAuth();

  if (process.env.REACT_APP_ENV === "production") {
    console.log = () => {};
    console.error = () => {}; // Optionally disable other console methods
    console.warn = () => {};
  } else {
    console.warn("Console Logs are enabled");
  }

  useEffect(() => {
    initializeAnalytics();
    logPageView();
    initializeClarity();
    setupAxiosInterceptors(logout);

    // Log page views on route changes
    const unlisten = () => {
      logPageView();
    };
    return () => {
      unlisten();
    };
  }, [logout]); // Ensure the interceptor setup runs when the logout function is available

  return (
    <div className="App">
      <VersionChecker />
      <LoadingBar isLoading={isLoading} />
      <Router>
        <div className="flex">
          <div className="flex-grow p-0">
            <Routes>
              <Route path="/" element={<Navigate to="/jobs/all" />} />

              {/* Public Routes */}
              <Route element={<PublicRoute />}>
                <Route path="/sign-up" element={<SignUp />} />
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* <Route path="/reset-password" element={<ResetPassword />} /> */}
              </Route>

              {/* Private Routes */}
              <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<SidebarLayout />}>
                  <Route index element={<ComingSoon />} />
                </Route>
                <Route path="/jobs" element={<SidebarLayout />}>
                  <Route path="" element={<Navigate to="/jobs/all" />} />
                  <Route path="create-new" element={<NewJob />} />
                  <Route path="edit/:jobId/:jobCode" element={<NewJob />} />
                  <Route
                    path="edit"
                    element={<Navigate to="/jobs/all" replace />}
                  />
                  <Route path=":jobStatus" element={<ExistingJob />} />
                  <Route
                    path=":jobStatus/:jobId/:jobCode"
                    element={<ViewJob />}
                  />
                  <Route
                    path=":jobStatus/:jobId/:jobCode/containers"
                    element={<ViewJob />}
                  />
                  <Route
                    path=":jobStatus/:jobId/:jobCode/containers/:tripId"
                    element={<Navigate to="live-tracking" replace />}
                  />
                  <Route
                    path=":jobStatus/:jobId/:jobCode/containers/:tripId/live-tracking"
                    element={<LiveTrack />}
                  />
                  <Route path="history" element={<JobHistory />} />
                  <Route path="history/upload" element={<JobHistoryUpload />} />
                </Route>

                {/* <Route path="/job/:jobId/:jobCode" element={<SidebarLayout />}>
                  <Route path="view" element={<ViewJob />} />
                  <Route path="edit" element={<NewJob />} />
                  <Route path="containers" element={<JobContainers />} />
                  <Route path="view/containers" element={<ViewJob />} />
                </Route> */}

                <Route
                  path="/job/:jobId/:jobCode/assign-containers"
                  element={<SidebarLayout />}
                >
                  <Route index element={<AssignContainers />} />
                </Route>
                <Route path="/transporters" element={<SidebarLayout />}>
                  <Route index element={<Transporters />} />
                </Route>
                <Route path="/drivers" element={<SidebarLayout />}>
                  <Route index element={<Drivers />} />
                </Route>
                <Route path="/instructions" element={<SidebarLayout />}>
                  <Route index element={<Instructions />} />
                </Route>
                <Route path="/locations" element={<SidebarLayout />}>
                  <Route index element={<ComingSoon />} />
                </Route>
                <Route path="/trips" element={<SidebarLayout />}>
                  <Route index element={<Navigate to="active" replace />} />
                  <Route path="active" element={<ActiveTrips />} />
                  {/* <Route path="active/:jobId/:jobCode" element={<ViewJob />} />
                  <Route
                    path="active/:jobId/:jobCode/:tripId/live-tracking"
                    element={<LiveTrack />}
                  /> */}
                  <Route path="history" element={<TripHistory />} />
                  {/* <Route path="history/upload" element={<ComingSoon />} /> */}
                  {/* <Route
                    path="history/upload"
                    element={<TripHistoryUpload />}
                  /> */}
                </Route>
                <Route
                  path="/trip-history/job/:jobId"
                  element={<SidebarLayout />}
                >
                  <Route index element={<TripHistoryJob />} />
                </Route>
                <Route path="/reports" element={<SidebarLayout />}>
                  <Route index element={<ComingSoon />} />
                </Route>
                <Route path="/profile" element={<SidebarLayout />}>
                  <Route index element={<Profile />} />
                </Route>
                <Route path="/settings" element={<SidebarLayout />}>
                  <Route index element={<ComingSoon />} />
                </Route>
                <Route path="/instructions" element={<SidebarLayout />}>
                  <Route index element={<Instructions />} />
                </Route>
              </Route>

              {/* Catch-all route for 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </div>
      </Router>
      <Toaster></Toaster>
    </div>
  );
};

export default App;
